import { createId } from "@paralleldrive/cuid2";
import { ClassRollLeaderboardCalculator, } from "../calculators/ClassRollLeaderboardCalculator";
// NOTE: there is deliberately no "Cancelled" status. SAC has no cancellation
// signal, and a lecture missing from a SAC response is not evidence that it
// was cancelled — it may just be an incomplete response. Lectures that drop
// out of SAC are marked with the app-owned `deletedInSAC` flag instead, which
// leaves the real status intact.
export var LectureStatus;
(function (LectureStatus) {
    LectureStatus["Created"] = "Created";
    LectureStatus["Started"] = "Started";
    LectureStatus["Completed"] = "Completed";
})(LectureStatus || (LectureStatus = {}));
export var WarmupGameStatus;
(function (WarmupGameStatus) {
    WarmupGameStatus["Active"] = "Active";
    WarmupGameStatus["Completed"] = "Completed";
    WarmupGameStatus["Cancelled"] = "Cancelled";
})(WarmupGameStatus || (WarmupGameStatus = {}));
export var LectureActivityType;
(function (LectureActivityType) {
    LectureActivityType["ScenarioView"] = "ScenarioView";
    LectureActivityType["FreeMode"] = "FreeMode";
    LectureActivityType["QuizSession"] = "QuizSession";
    LectureActivityType["TournamentRound"] = "TournamentRound";
    LectureActivityType["WarmupGames"] = "WarmupGames";
})(LectureActivityType || (LectureActivityType = {}));
export var LectureActivityStatus;
(function (LectureActivityStatus) {
    LectureActivityStatus["Active"] = "Active";
    LectureActivityStatus["Completed"] = "Completed";
    LectureActivityStatus["Cancelled"] = "Cancelled";
})(LectureActivityStatus || (LectureActivityStatus = {}));
export class Lecture {
    id;
    // --- SAC-synced ---
    cdid;
    classroomId;
    year;
    term;
    teacherId;
    firstName;
    lastName;
    lectureDate;
    lectureEndDate;
    topicsCovered;
    deletedInSAC;
    // --- App-editable, sourced from SAC API ---
    schoolName;
    groupName;
    // --- App-managed ---
    title;
    weekNumber; // obsolete — computed from lectureDate but not used in the app
    status;
    topicIds;
    homeworkAssigned;
    tournamentId;
    students;
    submittedRoll;
    autosavedRoll;
    leaderboard;
    warmupGames;
    activities;
    constructor(data) {
        this.id = data.cdid ?? createId();
        this.cdid = data.cdid;
        // SAC-synced
        this.classroomId = data.classroomId;
        this.year = data.year;
        this.term = data.term;
        this.teacherId = data.teacherId;
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.lectureDate = data.lectureDate;
        this.lectureEndDate = data.lectureEndDate;
        this.topicsCovered = data.topicsCovered;
        this.deletedInSAC = data.deletedInSAC ?? false;
        // App-editable, sourced from SAC API
        this.schoolName = data.schoolName;
        this.groupName = data.groupName;
        // App-managed
        this.title = data.title;
        this.weekNumber = data.weekNumber;
        this.status = data.status;
        this.topicIds = data.topicIds;
        this.homeworkAssigned = data.homeworkAssigned;
        this.tournamentId = data.tournamentId;
        this.students = data.students || [];
        this.submittedRoll = data.submittedRoll || undefined;
        this.autosavedRoll = data.autosavedRoll || undefined;
        this.leaderboard = data.leaderboard || undefined;
        this.warmupGames = data.warmupGames || [];
        this.activities = data.activities || [];
    }
    update(data) {
        if (data.classroomId !== undefined)
            this.classroomId = data.classroomId;
        if (data.topicIds !== undefined)
            this.topicIds = data.topicIds;
        if (data.weekNumber !== undefined)
            this.weekNumber = data.weekNumber;
        if (data.lectureDate !== undefined)
            this.lectureDate = data.lectureDate;
        if (data.lectureEndDate !== undefined)
            this.lectureEndDate = data.lectureEndDate;
        if (data.homeworkAssigned !== undefined)
            this.homeworkAssigned = data.homeworkAssigned;
    }
    start(teacherId) {
        if (this.status !== LectureStatus.Created) {
            throw new Error("Lecture can only be started when in Created status");
        }
        this.teacherId = teacherId;
        this.status = LectureStatus.Started;
    }
    complete() {
        if (this.status !== LectureStatus.Started) {
            throw new Error("Lecture can only be completed when in Started status");
        }
        this.status = LectureStatus.Completed;
    }
    submitRoll() {
        // Create a deep copy of the current students array
        this.submittedRoll = JSON.parse(JSON.stringify(this.students));
    }
    autoSaveRoll() {
        // Create a deep copy of the current students array for auto-snapshot
        if (!this.autosavedRoll) {
            this.autosavedRoll = JSON.parse(JSON.stringify(this.students));
        }
    }
    addTopic(topicId) {
        if (this.status === LectureStatus.Completed) {
            throw new Error("Cannot modify topics in a completed lecture");
        }
        if (!this.topicIds) {
            this.topicIds = [];
        }
        if (!this.topicIds.includes(topicId)) {
            this.topicIds.push(topicId);
        }
    }
    removeTopic(topicId) {
        if (this.status === LectureStatus.Completed) {
            throw new Error("Cannot modify topics in a completed lecture");
        }
        if (!this.topicIds) {
            return;
        }
        this.topicIds = this.topicIds.filter((id) => id !== topicId);
    }
    addWarmupGame(gameData) {
        if (this.status !== LectureStatus.Started) {
            throw new Error("Warm-up games can only be added during started lecture");
        }
        const warmupGame = {
            id: createId(),
            lectureId: this.id,
            whiteUsername: gameData.whiteUsername,
            blackUsername: gameData.blackUsername,
            whiteDisplayName: gameData.whiteDisplayName,
            blackDisplayName: gameData.blackDisplayName,
            status: WarmupGameStatus.Active,
            startTime: new Date(),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        this.warmupGames.push(warmupGame);
        return warmupGame;
    }
    updateWarmupGameResult(gameId, whiteResult, blackResult) {
        const game = this.warmupGames.find((g) => g.id === gameId);
        if (!game) {
            throw new Error(`Warm-up game with id ${gameId} not found`);
        }
        game.whiteResult = whiteResult;
        game.blackResult = blackResult;
        game.status = WarmupGameStatus.Completed;
        game.finishTime = new Date();
        game.updatedAt = new Date();
    }
    cancelWarmupGame(gameId) {
        const game = this.warmupGames.find((g) => g.id === gameId);
        if (!game) {
            throw new Error(`Warm-up game with id ${gameId} not found`);
        }
        game.status = WarmupGameStatus.Cancelled;
        game.finishTime = new Date();
        game.updatedAt = new Date();
    }
    getActiveWarmupGames() {
        return this.warmupGames.filter((g) => g.status === WarmupGameStatus.Active);
    }
    getWarmupGamesByPlayer(username) {
        return this.warmupGames.filter((g) => g.whiteUsername === username || g.blackUsername === username);
    }
    isPlayerInWarmupGame(username) {
        return this.getActiveWarmupGames().some((g) => g.whiteUsername === username || g.blackUsername === username);
    }
    /**
     * Calculate and save the leaderboard based on current students
     */
    calculateLeaderboard() {
        if (!this.students || this.students.length === 0) {
            this.leaderboard = [];
            return;
        }
        // Convert students to the format expected by the calculator
        const lectureStudents = this.students.map((student) => ({
            lectureId: this.id,
            date: this.lectureDate.toISOString(),
            studentId: student.studentId,
            firstName: student.firstName,
            lastName: student.lastName,
            performanceInput: {
                awards: student.awards || [],
                score: student.score || 0,
                // Tournament properties are not available in LectureStudent, so default to 0
                tournamentWins: 0,
                tournamentDraws: 0,
                tournamentLosses: 0,
            },
        }));
        // Calculate the leaderboard
        this.leaderboard =
            ClassRollLeaderboardCalculator.calculateStudentScoreBreakdowns(lectureStudents);
    }
    // ============================================================================
    // Static methods for working with plain lecture objects (from Vuex)
    // ============================================================================
    /**
     * Check if we should record activities for a lecture object
     */
    static shouldRecordActivity(lecture) {
        if (lecture.status !== LectureStatus.Started) {
            return false;
        }
        const now = new Date();
        const lectureDate = new Date(lecture.lectureDate);
        const lectureDay = new Date(lectureDate.getFullYear(), lectureDate.getMonth(), lectureDate.getDate());
        const currentDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return lectureDay.getTime() === currentDay.getTime();
    }
    /**
     * Start a new activity on a lecture object
     * Modifies the lecture object in place and returns the created activity
     */
    static startActivity(lecture, type, metadata) {
        if (!Lecture.shouldRecordActivity(lecture)) {
            return null;
        }
        // Initialize activities array if it doesn't exist
        if (!lecture.activities) {
            lecture.activities = [];
        }
        // End any active activity of the same type
        const activeActivity = lecture.activities.find((a) => a.type === type && a.status === LectureActivityStatus.Active);
        if (activeActivity) {
            Lecture.endActivity(lecture, activeActivity.id);
        }
        const now = new Date();
        const activity = {
            id: createId(),
            type,
            status: LectureActivityStatus.Active,
            startTime: now,
            endTime: null,
            createdAt: now,
            updatedAt: now,
            ...metadata,
        };
        lecture.activities.push(activity);
        return activity;
    }
    /**
     * End an activity by ID on a lecture object
     * Modifies the lecture object in place and returns the updated activity
     */
    static endActivity(lecture, activityId, metadata) {
        if (!lecture.activities) {
            return null;
        }
        const activity = lecture.activities.find((a) => a.id === activityId);
        if (!activity) {
            return null;
        }
        const now = new Date();
        activity.status = LectureActivityStatus.Completed;
        activity.endTime = now;
        activity.updatedAt = now;
        // Update metadata if provided
        if (metadata) {
            Object.assign(activity, metadata);
        }
        return activity;
    }
    /**
     * Get the currently active activity of a specific type from a lecture object
     */
    static getCurrentActivity(lecture, type) {
        if (!lecture.activities) {
            return null;
        }
        if (type) {
            return (lecture.activities.find((a) => a.type === type && a.status === LectureActivityStatus.Active) || null);
        }
        // Return any active activity
        return (lecture.activities.find((a) => a.status === LectureActivityStatus.Active) || null);
    }
    /**
     * End all active activities on a lecture object
     */
    static endAllActiveActivities(lecture) {
        if (!lecture.activities) {
            return;
        }
        const activeActivities = lecture.activities.filter((a) => a.status === LectureActivityStatus.Active);
        const now = new Date();
        activeActivities.forEach((activity) => {
            activity.status = LectureActivityStatus.Completed;
            activity.endTime = now;
            activity.updatedAt = now;
        });
    }
}
